import React from 'react';
import { User, Phone, MapPin, Mail } from 'lucide-react';
import { typography } from '../styles/design-tokens';

// Define the Address interface to match GuardianAddressDto structure
interface Address {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

// Helper function to format address
const getFullAddress = (address: Address | null | undefined): string => {
  if (!address) return 'No address available';

  // First check if all values are N/A or empty
  const isAllNA = Object.values(address).every(
    (val) => !val || val === 'N/A' || val.trim() === '',
  );

  if (isAllNA) return 'No address available';

  // Filter out N/A or empty values
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
    address.country,
  ].filter((part) => {
    return part && part !== 'N/A' && part.trim() !== '';
  });

  if (parts.length === 0) return 'No address available';

  return parts.join(', ');
};

interface GuardianCardProps {
  guardian: {
    name: string;
    email: string | null;
    relation: string;
    contact?: string | null;
    address?: Address | null;
    profileImage?: string;
    isPrimaryGuardian?: boolean;
    isEmergencyContact?: boolean;
  };
}

export const GuardianCard = ({ guardian }: GuardianCardProps) => (
  <div className="bg-[#F5F8F9] rounded-xl p-4 relative">
    <div className="flex flex-col sm:flex-row items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-[#E2FDCB] flex-shrink-0 flex items-center justify-center overflow-hidden">
        {guardian.profileImage ? (
          <img
            src={guardian.profileImage}
            alt={guardian.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <User size={24} className="text-[#0D373D]" />
        )}
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex flex-wrap w-full gap-2">
          <div className="flex flex-col flex-shrink w-full">
            <div className="flex flex-row flex-wrap justify-between w-full gap-2 mb-2">
              <h5
                className={`${typography.text.body.large} font-medium text-[#0D373D] `}
              >
                {guardian.name}
              </h5>
              <div className="flex flex-row gap-2 w-fit">
                {guardian.isPrimaryGuardian && (
                  <span
                    className={`inline-flex px-2 py-0.5 bg-[#E2FDCB] text-[#0D373D] rounded-lg ${typography.text.label} whitespace-nowrap`}
                  >
                    Primary Guardian
                  </span>
                )}
                {guardian.isEmergencyContact && (
                  <span
                    className={`inline-flex px-2 py-0.5 bg-[#FFEAEA] text-[#D32F2F] rounded-lg ${typography.text.label} whitespace-nowrap`}
                  >
                    Emergency Contact
                  </span>
                )}
              </div>
            </div>
            <span className={`${typography.text.body.small} text-[#4D4F52]`}>
              {guardian.relation.toLocaleLowerCase()}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`${typography.text.label} w-16 flex-shrink-0`}>
              Contact
            </span>
            <span
              className={`flex items-center gap-1 ${typography.text.body.default}`}
            >
              <Phone size={14} className="flex-shrink-0" />
              <span className="truncate">{guardian.contact || 'N/A'}</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`${typography.text.label} w-16 flex-shrink-0`}>
              Email
            </span>
            <span className={`flex items-center gap-1 ${typography.text.body.default}`}>
              <Mail size={14} className="flex-shrink-0" />
              <span className="truncate">{guardian.email || 'N/A'}</span>
            </span>
          </div>
          <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
            <span
              className={`${typography.text.label} w-16 flex-shrink-0 mt-1`}
            >
              Address
            </span>
            <span
              className={`flex items-start gap-1 break-words flex-1 ${typography.text.body.default}`}
            >
              <MapPin size={14} className="flex-shrink-0 mt-1" />
              <span className="break-words">
                {getFullAddress(guardian.address)}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);
